import React from 'react';
import { Grid, Skeleton, Box, Stack, Card, CardContent, useTheme } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const SettingsPageSkeleton = () => {
  const theme = useTheme();
  const GRID_SPACING = { xs: 2, sm: 2, md: 3 };

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 2, md: 3 },
          '&:last-child': { pb: { xs: 2, md: 3 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  const InputGroupSkeleton = () => (
    <Box width="100%">
      <Skeleton variant="text" width={100} height={24} sx={{ mb: 1 }} />
      <Skeleton variant="rectangular" height={45} width="100%" sx={{ borderRadius: 1 }} />
    </Box>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, md: 3 }}>
        <Skeleton variant="text" width={120} height={40} />
        <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: 1 }} />
      </Box>

      <Grid container spacing={GRID_SPACING}>
        <Grid item xs={12} md={4} lg={3} maxHeight={480}>
          <SkeletonCard>
            <Stack spacing={3} alignItems="center">
              <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                <Skeleton variant="circular" width={100} height={100} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1.5 }} />
                <Skeleton variant="rectangular" width={100} height={30} sx={{ borderRadius: 4 }} />
              </Box>

              <Stack spacing={2} width="100%">
                {[...Array(3)].map((_, i) => (
                  <Box key={i}>
                    <Skeleton variant="text" width="70%" height={24} />
                  </Box>
                ))}
              </Stack>
            </Stack>
          </SkeletonCard>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <SkeletonCard>
            <Box display="flex" alignItems="center" gap={1}>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="text" width={200} height={32} />
            </Box>

            <Grid container spacing={{ xs: 1, md: 2 }}>
              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputGroupSkeleton />
              </Grid>

              <Grid item xs={12}>
                <InputGroupSkeleton />
              </Grid>
            </Grid>

            <Box pt={1}>
              <Skeleton variant="rectangular" width={140} height={45} sx={{ borderRadius: 1 }} />
            </Box>
          </SkeletonCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPageSkeleton;
